import React from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage, useIntl } from 'react-intl';
import { Row, Col, Panel, Label } from 'react-bootstrap';
import cx from 'classnames';
import withStyles from 'isomorphic-style-loader/withStyles';

import InboxItem from './InboxItem';
import Loader from '../Loader';
import messages from '../../locale/jsonMessages';
import s from './Inbox.css';

const UnreadMessages = ({ userId, loading, getUnreadThreads }) => {
	const { formatMessage } = useIntl();

	const label = (status, noStyle = false) => {
		let style, labelContent;

		switch (status) {
			case 'inquiry':
				labelContent = <FormattedMessage {...messages.messageStatus1} />;
				style = 'info';
				break;
			case 'preApproved':
				labelContent = <FormattedMessage {...messages.messageStatus2} />;
				style = 'primary';
				break;
			case 'declined':
				labelContent = <FormattedMessage {...messages.messageStatus3} />;
				style = 'danger';
				break;
			case 'approved':
				labelContent = <FormattedMessage {...messages.messageStatus4} />;
				style = 'success';
				break;
			case 'pending':
				labelContent = <FormattedMessage {...messages.messageStatus5} />;
				style = 'warning';
				break;
			case 'cancelledByHost':
			case 'cancelledByGuest':
				labelContent = status === 'cancelledByHost'
					? <FormattedMessage {...messages.messageStatus6} />
					: <FormattedMessage {...messages.messageStatus7} />;
				style = 'danger';
				break;
			case 'intantBooking':
			case 'confirmed':
				labelContent = <FormattedMessage {...messages.messageStatus8} />;
				style = 'success';
				break;
			case 'expired':
				labelContent = <FormattedMessage {...messages.messageStatus9} />;
				style = 'danger';
				break;
			case 'requestToBook':
				labelContent = <FormattedMessage {...messages.messageStatus10} />;
				style = 'primary';
				break;
			case 'completed':
				labelContent = <FormattedMessage {...messages.panelHeader2} />;
				style = 'success';
				break;
			default:
				return null;
		}

		return noStyle ? labelContent : <Label bsStyle={style}>{labelContent}</Label>;
	};

	if (loading) {
		return <Loader type="text" />;
	}

	if (!getUnreadThreads?.length) {
		return <div />;
	}

	return (
		<div className={cx(s.progressContainerResponsive, s.space5, s.spaceTop5)}>
			<Row>
				<Col xs={12}>
					<Panel className={cx('dashboardMessage', s.panelHeader)}>
						<ul className={cx(s.listLayout, 'listContainerRTL')}>
							{getUnreadThreads.map((item, index) => {
								const type = userId === item.host ? 'owner' : 'renter';

								if (item?.threadItemUnread && item?.guestProfile && item?.hostProfile) {
									const profile = type === 'owner' ? item.guestProfile : item.hostProfile;

									return (
										<InboxItem
											key={index}
											threadId={item.id}
											type={type}
											profileId={profile.profileId}
											picture={profile.picture}
											displayName={profile.displayName}
											content={
												item.threadItemUnread.content ??
												label(item.threadItemUnread.type, true)
											}
											createdAt={item.threadItemUnread.createdAt}
											city={item.listData.city}
											state={item.listData.state}
											country={item.listData.country}
											startDate={item.threadItemUnread.startDate}
											endDate={item.threadItemUnread.endDate}
											status={label(item.threadItemUnread.type)}
											sentBy={item.threadItemUnread.sentBy}
											read={item.threadItemUnread.isRead}
										/>
									);
								} else {
									return <li key={index} />;
								}
							})}
						</ul>
					</Panel>
				</Col>
			</Row>
		</div>
	);
};

UnreadMessages.propTypes = {
	userId: PropTypes.string.isRequired,
	loading: PropTypes.bool.isRequired,
	getUnreadThreads: PropTypes.arrayOf(
		PropTypes.shape({
			id: PropTypes.number.isRequired,
			listData: PropTypes.shape({
				city: PropTypes.string.isRequired,
				state: PropTypes.string.isRequired,
				country: PropTypes.string.isRequired,
			}),
			guestProfile: PropTypes.shape({
				profileId: PropTypes.number.isRequired,
				picture: PropTypes.string,
				displayName: PropTypes.string.isRequired,
			}),
			hostProfile: PropTypes.shape({
				profileId: PropTypes.number.isRequired,
				picture: PropTypes.string,
				displayName: PropTypes.string.isRequired,
			}),
			threadItemUnread: PropTypes.shape({
				type: PropTypes.string.isRequired,
				content: PropTypes.string,
				startDate: PropTypes.string,
				endDate: PropTypes.string,
				isRead: PropTypes.bool.isRequired,
				sentBy: PropTypes.string.isRequired,
				createdAt: PropTypes.string.isRequired,
			}),
		})
	),
};

UnreadMessages.defaultProps = {
	loading: true,
	getUnreadThreads: [],
};

export default (withStyles(s)(UnreadMessages));
